import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { formatCents } from '@/lib/format/currency';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import { formatMetric } from '@/lib/social-analytics/formatMetric';
import type { CpmSubmissionRow } from './CpmCampaignsTable';

const safeExternalUrl = (value: string | null | undefined) => {
  try {
    const url = new URL(value ?? '');
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
};

const safeCampaignPath = (documentId: string | null | undefined) => {
  if (!documentId || /^\d+$/.test(documentId) || !/^[A-Za-z0-9_-]+$/.test(documentId)) return null;
  return `/campaigns/${encodeURIComponent(documentId)}`;
};

const cpmStatus = (row: Pick<CpmSubmissionRow, 'status' | 'disqualified'>) => {
  if (row.disqualified) return 'disqualified';
  if (row.status === 'approved' || row.status === 'disapproved') return row.status;
  if (row.status === 'neutral' || row.status === null || row.status === undefined) return 'pending';
  return 'unknown';
};

const MobileStatusBadge = ({ row }: { row: CpmSubmissionRow }) => {
  const { t } = useTranslation();
  const status = cpmStatus(row);
  const className = status === 'approved'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : status === 'disapproved' || status === 'disqualified'
      ? 'border-destructive/30 bg-destructive/10 text-destructive'
      : status === 'pending'
        ? 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300'
        : 'border-border bg-muted/60 text-muted-foreground';
  return <Badge variant="outline" className={className}>{t(`analytics.cpm.status.${status}`)}</Badge>;
};

export const CpmCampaignMobileCards = ({ rows }: { rows: readonly CpmSubmissionRow[] }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  return <div className="space-y-3 lg:hidden">{rows.map((row) => {
    const campaignPath = safeCampaignPath(row.campaign?.id);
    const postUrl = safeExternalUrl(row.url);
    const campaignName = row.campaign?.name || t('analytics.cpm.unnamedCampaign');
    return <article key={row.id} className="rounded-xl border border-border/70 bg-card p-4">
      <dl>
        <div className="flex items-start gap-3"><div className="min-w-0 flex-1"><dt className="sr-only">{t('analytics.cpm.columns.campaign')}</dt><dd className="truncate text-sm font-semibold">{campaignPath ? <Link to={campaignPath} className="hover:underline">{campaignName}</Link> : campaignName}</dd></div>{postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" aria-label={t('analytics.cpm.openPost')} className="shrink-0 text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></a>}</div>
        <div className="mt-1 flex items-center justify-between gap-2"><div><dt className="sr-only">{t('analytics.cpm.columns.account')}</dt><dd className="text-xs text-muted-foreground">@{row.account?.username || '—'} · {t(`analytics.platforms.${row.account?.platform ?? row.platform}`)}</dd></div><div><dt className="sr-only">{t('analytics.cpm.columns.status')}</dt><dd><MobileStatusBadge row={row} /></dd></div></div>
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border/60 pt-3 text-xs"><div><dt className="text-muted-foreground">{t('analytics.cpm.payoutQualifiedViews')}</dt><dd className="mt-0.5 font-semibold tabular-nums">{formatMetric(row.payout_qualified_views ?? null, locale)}</dd></div><div><dt className="text-muted-foreground">{t('analytics.cpm.columns.accumulatedPayout')}</dt><dd className="mt-0.5 font-semibold tabular-nums">{Number.isFinite(row.accumulated_payout_cents) ? formatCents(row.accumulated_payout_cents, 'BRL') : '—'}</dd></div><div className="col-span-2"><dt className="text-muted-foreground">{t('analytics.cpm.columns.lastSync')}</dt><dd className="mt-0.5 font-medium">{formatAnalyticsSyncDate(row.last_cpm_synced_at, locale, '—')}</dd></div></div>
      </dl>
    </article>;
  })}</div>;
};
